import { Metadata } from 'nice-grpc-web';
import { Middleware } from 'redux';
import { AppDispatch, RootState } from '../store';

import {
    NetPositionEvent,
    NetPositionUpdateEvent
} from '@/compiled_proto/com/celertech/positionmanager/api/pnl/PostionManagerServiceProto';
import { forEachAsync } from '@/helpers/asyncIterableHelper';
import { positionManagerClient } from '@/helpers/environmentHelper';
import { Logger } from '@/utils/logger';
import { v4 as uuidv4 } from 'uuid';
import { controlClearSubscriptions, controlInitSubscriptions, User } from '../reducers/authSlice';
import { setCurrencyPosition, setOpenPositions } from '../reducers/openPositionSlice';

let positionSubscriptionId: any = null;
let unrealisedPnLSubscriptionId: any = null;
let positionsSubscription: any = null;
let unrealisedPnLSubscription: any = null;
let abortController = new AbortController();

const setupPositionSubscription = async (store: any, dispatch: AppDispatch, credentials: User) => {
    // Subscribe for changes to openPositions
    positionsSubscription = positionManagerClient.ws.subscribeForPositions(
        { accounts: [credentials.accounts.map((acc) => acc.code).join(',')] },
        {
            metadata: Metadata({ 'authorization-token': credentials.authToken }),
            signal: abortController.signal
        }
    );

    const currentSubscriptionId = uuidv4();
    positionSubscriptionId = currentSubscriptionId;
    forEachAsync(
        positionsSubscription,
        (openPositionUpdate: NetPositionUpdateEvent) => {
            const positionEvent = openPositionUpdate.positionEvent;

            if (positionEvent) {
                if (!positionEvent.positionUpdate?.shouldDelete) {
                    dispatch(setCurrencyPosition(positionEvent));
                }
            }
        },
        abortController,
        'OpenPositionMiddleware'
    );
};

const setupUnrealisedPnLSubscription = async (store: any, dispatch: AppDispatch, credentials: User) => {
    // Subscribe for changes to unrealisedPnL
    unrealisedPnLSubscription = positionManagerClient.ws.subscribeForUnrealisedPnl(
        { throttleDelayInMillis: '0' },
        {
            metadata: Metadata({ 'authorization-token': credentials.authToken }),
            signal: abortController.signal
        }
    );

    const currentSubscriptionId = uuidv4();
    unrealisedPnLSubscriptionId = currentSubscriptionId;
    forEachAsync(
        unrealisedPnLSubscription,
        (openPositionUpdate: { event: NetPositionUpdateEvent[]; isHeartBeat: boolean }) => {
            const unrealisedPnL = openPositionUpdate.event
                .map((e) => e.positionEvent)
                .filter((e) => e && !e.positionUpdate?.shouldDelete) as NetPositionEvent[];
            if (unrealisedPnL.length > 0) dispatch(setOpenPositions(unrealisedPnL));
        },

        abortController,
        'OpenPositionMiddleware'
    );
};

const openPositionMiddleware: Middleware = (store) => (next) => (action) => {
    if (controlClearSubscriptions.match(action)) {
        abortController.abort();
        positionSubscriptionId = null;
        unrealisedPnLSubscriptionId = null;
        positionsSubscription = null;
        unrealisedPnLSubscription = null;
    } else if (controlInitSubscriptions.match(action)) {
        Logger({ title: `OpenPositionMiddleware: Initialise Subscriptions`, callback: () => {} });

        const dispatch: AppDispatch = store.dispatch;
        const state: RootState = store.getState();
        const creds = state.auth.user;
        // If not already listening, setup subscriptions
        if (!positionsSubscription && !unrealisedPnLSubscription && creds) {
            abortController = new AbortController();
            setupPositionSubscription(store, dispatch, creds);
            setupUnrealisedPnLSubscription(store, dispatch, creds);
        }
    }

    // Pass on to next middlewares in line
    next(action);
};

export default openPositionMiddleware;
